import React from "react"
import { Box, Grid, Link, Sheet, Typography } from "@mui/joy"
import {
    HighlightBox,
    LegalFooter,
    LegalHeader,
    LegalHero,
    LegalMain,
    LegalPage,
    LegalSection,
    LegalText,
    LegalToc,
} from "../components/LegalLayout"

// ─── Retention card ────────────────────────────────────────────────────────

type RetentionCardProps = {
    label: string
    period: string
    desc: string
}

const RetentionCard = ({ label, period, desc }: RetentionCardProps) => (
    <Sheet
        variant="outlined"
        sx={{ borderRadius: "8px", p: 2, bgcolor: "#fff" }}
    >
        <Typography
            sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "var(--ls-muted)",
                mb: 0.5,
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{ fontSize: "1.1rem", fontWeight: 600, color: "var(--ls-navy)", mb: 0.3 }}
        >
            {period}
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: "var(--ls-muted)" }}>
            {desc}
        </Typography>
    </Sheet>
)

// ─── TOC ───────────────────────────────────────────────────────────────────

const tocItems = [
    { href: "#intro", label: "Introduction" },
    { href: "#account", label: "Account Data" },
    { href: "#exam", label: "Exam & Session Data" },
    { href: "#audio", label: "Audio Recordings" },
    { href: "#payment", label: "Payment & Transaction Data" },
    { href: "#communications", label: "Communication Data" },
    { href: "#device", label: "Device & Usage Data" },
    { href: "#tokens", label: "Push Notification Tokens" },
    { href: "#deletion", label: "Data Deletion Requests" },
    { href: "#security", label: "Data Security" },
    { href: "#changes", label: "Changes to This Policy" },
]

const retentionCards: RetentionCardProps[] = [
    { label: "Account data",     period: "Active + 30 days", desc: "Deleted 30 days after account closure" },
    { label: "Exam data",        period: "12 months",        desc: "After subscription ends" },
    { label: "Audio files",      period: "90 days",          desc: "After grading is complete" },
    { label: "Payment records",  period: "7 years",          desc: "Financial compliance requirement" },
    { label: "Usage logs",       period: "90 days",          desc: "Security monitoring only" },
    { label: "Support messages", period: "2 years",          desc: "Issue tracking and resolution" },
]

// ─── Page ──────────────────────────────────────────────────────────────────

const DataRetention = () => (
    <LegalPage>
        <LegalHeader />
        <LegalHero title="Data Retention Policy" />

        <LegalMain>
            <LegalToc items={tocItems} />

            {/* Summary cards */}
            <Grid container spacing={1.5} sx={{ mb: 5 }}>
                {retentionCards.map((card) => (
                    <Grid key={card.label} xs={12} sm={6} md={4}>
                        <RetentionCard {...card} />
                    </Grid>
                ))}
            </Grid>

            <LegalSection id="intro" num="01" title="Introduction">
                <LegalText>
                    This Data Retention Policy explains how LinguaScore, operated by ZeeJay Labs, collects, stores,
                    and deletes user data. We are committed to retaining data only for as long as necessary to provide
                    our services and meet our legal obligations.
                </LegalText>
            </LegalSection>

            <LegalSection id="account" num="02" title="Account Data">
                <LegalText>
                    When you create an account on LinguaScore, we collect and retain your name, email address,
                    authentication provider information, and profile details for as long as your account remains
                    active. If you delete your account, this data is permanently removed from our systems within 30
                    days of the deletion request.
                </LegalText>
            </LegalSection>

            <LegalSection id="exam" num="03" title="Exam & Session Data">
                <LegalText>
                    Your exam attempts, session recordings, written responses, and grading results are retained for
                    the duration of your active subscription and for a period of 12 months after your subscription
                    ends. This allows you to access your historical results and track your progress over time. After
                    this period, session data is permanently deleted.
                </LegalText>
            </LegalSection>

            <LegalSection id="audio" num="04" title="Audio Recordings">
                <HighlightBox>
                    <Typography>
                        Speaking test audio recordings are used solely for AI-powered grading and feedback. Audio
                        files are permanently deleted within 90 days of grading completion and are never used for any
                        other purpose.
                    </Typography>
                </HighlightBox>
                <LegalText>
                    We do not share your audio recordings with third parties beyond our transcription service
                    provider, and no human reviewer listens to your recordings.
                </LegalText>
            </LegalSection>

            <LegalSection id="payment" num="05" title="Payment & Transaction Data">
                <LegalText>
                    Transaction records, including payment amounts, dates, subscription tiers, and payment provider
                    references, are retained for a period of 7 years in compliance with applicable financial
                    regulations. This data is securely stored and accessible only to authorized personnel for
                    accounting and legal purposes.
                </LegalText>
            </LegalSection>

            <LegalSection id="communications" num="06" title="Communication Data">
                <LegalText>
                    If you contact our support team via email or any other channel, we retain those communications
                    for a period of 2 years to allow us to track the history of any issues or requests you may have
                    raised.
                </LegalText>
            </LegalSection>

            <LegalSection id="device" num="07" title="Device & Usage Data">
                <LegalText>
                    Technical data such as device type, browser type, IP address, and usage logs are retained for a
                    maximum of 90 days for security monitoring and performance improvement purposes, after which they
                    are automatically deleted.
                </LegalText>
            </LegalSection>

            <LegalSection id="tokens" num="08" title="Push Notification Tokens">
                <LegalText>
                    Firebase Cloud Messaging tokens used to deliver push notifications are retained for as long as
                    your account is active. Tokens that have not been used or refreshed for a period of 6 months are
                    automatically removed from our systems.
                </LegalText>
            </LegalSection>

            <LegalSection id="deletion" num="09" title="Data Deletion Requests">
                <LegalText>
                    You have the right to request the deletion of your personal data at any time. To submit a
                    deletion request, contact us at{" "}
                    <Link href="mailto:privacy@linguascores.com" sx={{ color: "var(--ls-pink)" }}>
                        privacy@linguascores.com
                    </Link>. We will process your request within 30 days and confirm once deletion is complete.
                    Please note that some data may be retained beyond this period where required by law.
                </LegalText>
            </LegalSection>

            <LegalSection id="security" num="10" title="Data Security">
                <LegalText>
                    All retained data is stored securely using industry-standard encryption. Access to personal data
                    is restricted to authorized personnel on a need-to-know basis. We conduct regular security
                    reviews to ensure your data remains protected.
                </LegalText>
            </LegalSection>

            <LegalSection id="changes" num="11" title="Changes to This Policy">
                <LegalText>
                    We may update this Data Retention Policy from time to time. We will notify you of any significant
                    changes via email or through the LinguaScore platform.
                </LegalText>
                <HighlightBox>
                    <Typography>
                        Questions? Contact us at{" "}
                        <Link href="mailto:privacy@linguascores.com" sx={{ color: "var(--ls-pink)" }}>
                            privacy@linguascores.com
                        </Link>{" "}
                        — ZeeJay Labs, Douala, Cameroon.
                    </Typography>
                </HighlightBox>
            </LegalSection>
        </LegalMain>

        <LegalFooter />
    </LegalPage>
)

export default DataRetention
